import cls from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserTenantOptionFormData } from "../types";
// components
import { Button, Divider, Form, Input, Select, Tabs } from "antd";
import { ToTopOutlined, VerticalAlignBottomOutlined } from "@ant-design/icons";

export const Step1Form: React.FC<{
  data: UserTenantOptionFormData;
  visible: boolean;
  onPrev: () => void;
  onNext: (data: UserTenantOptionFormData) => void;
  onCancel: () => void;
}> = ({ data, visible, onPrev, onNext, onCancel }) => {
  const { t } = useTranslation();

  const [activeTabKey, setActiveTabKey] = useState("deposit");

  const [form] = Form.useForm<UserTenantOptionFormData>();

  const getDepositRateModeText = (value: number) => {
    if (value === 0) return t("domains.Transfer.exchangeRate");
    else if (value === 1) return t("domains.Transfer.addSub");
    return "";
  };

  const [depositRateModeText, setDepositRateModeText] = useState<string>(
    getDepositRateModeText(data.tenant_deposit_rate_mode)
  );

  const handleDepositRateModeChange = (value: number) => {
    setDepositRateModeText(getDepositRateModeText(value));
  };

  const getDepositChargeMode = (value: number) => {
    return value;
  };

  const [depositChargeMode, setDepositChargeMode] = useState<number>(
    getDepositChargeMode(data.tenant_deposit_charge_mode)
  );

  const handleDepositChargeModeChange = (value: number) => {
    setDepositChargeMode(getDepositChargeMode(value));
  };

  const getWithdrawRateModeText = (value: number) => {
    if (value === 0) return t("domains.Transfer.exchangeRate");
    else if (value === 1) return t("domains.Transfer.addSub");
    return "";
  };

  const [withdrawRateModeText, setWithdrawRateModeText] = useState<string>(
    getWithdrawRateModeText(data.tenant_withdraw_rate_mode)
  );

  const handleWithdrawRateModeChange = (value: number) => {
    setWithdrawRateModeText(getWithdrawRateModeText(value));
  };

  const getWithdrawChargeMode = (value: number) => {
    return value;
  };

  const [withdrawChargeMode, setWithdrawChargeMode] = useState<number>(
    getWithdrawChargeMode(data.tenant_withdraw_charge_mode)
  );

  const handleWithdrawChargeModeChange = (value: number) => {
    setWithdrawChargeMode(getDepositChargeMode(value));
  };

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(data);
  }, [data, form]);

  return (
    <Form
      form={form}
      labelCol={{ span: 6 }}
      className={cls(["mt:16", { hidden: !visible }])}
      onFinish={(values) => {
        onNext(values);
      }}
      onFinishFailed={({ errorFields }) => {
        const depositErrors = errorFields.filter((field) =>
          field.name.some((n) => (n as string).includes("deposit"))
        );

        if (depositErrors.length > 0) setActiveTabKey("deposit");
        else setActiveTabKey("withdraw");
      }}
    >
      <Tabs
        className="pl:16"
        activeKey={activeTabKey}
        items={[
          {
            label: t("domains.Tenant.tenantDeposit"),
            key: "deposit",
            forceRender: true,
            icon: <VerticalAlignBottomOutlined />,
            children: (
              <>
                <Form.Item
                  required
                  label={t("domains.Tenant.tenantDepositExpired")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="tenant_deposit_expired"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantDepositExpired"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.tenantDepositExpired"),
                        })}
                      />
                    </Form.Item>
                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.minute")}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  required
                  label={t("domains.Tenant.tenantDepositRate")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="tenant_deposit_rate_mode"
                    >
                      <Select onChange={handleDepositRateModeChange}>
                        <Select.Option value={0}>
                          {t("domains.Transfer.fixedExchangeRate")}
                        </Select.Option>
                        <Select.Option value={1}>
                          {t("domains.Transfer.followBito")}
                        </Select.Option>
                      </Select>
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center">
                      {depositRateModeText}
                    </div>

                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="tenant_deposit_rate_data"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantDepositRate"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.tenantDepositRate"),
                        })}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>

                <Form.Item
                  required
                  label={t("domains.Tenant.tenantDepositCharge")}
                  className="mb:0"
                >
                  <div className="flex ai:center gap:16">
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="tenant_deposit_charge_mode"
                    >
                      <Select onChange={handleDepositChargeModeChange}>
                        <Select.Option value={0}>
                          {t("domains.Transfer.unit")}
                        </Select.Option>
                        <Select.Option value={1}>
                          {t("domains.Transfer.ratio")}
                        </Select.Option>
                        <Select.Option value={2}>
                          {t("domains.Transfer.unitAndRatio")}
                        </Select.Option>
                      </Select>
                    </Form.Item>

                    {depositChargeMode === 0 && (
                      <Form.Item required className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="tenant_deposit_charge_data"
                            rules={[
                              {
                                required: true,
                                message: t("systems.Error.cannotBeEmpty", {
                                  field: t(
                                    "domains.Tenant.tenantDepositCharge"
                                  ),
                                }),
                              },
                            ]}
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.tenantDepositCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            USDT
                          </div>
                        </div>
                      </Form.Item>
                    )}

                    {depositChargeMode === 1 && (
                      <Form.Item required className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="tenant_deposit_charge_rate"
                            rules={[
                              {
                                required: true,
                                message: t("systems.Error.cannotBeEmpty", {
                                  field: t(
                                    "domains.Tenant.tenantDepositCharge"
                                  ),
                                }),
                              },
                            ]}
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.tenantDepositCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            %
                          </div>
                        </div>
                      </Form.Item>
                    )}

                    {depositChargeMode === 2 && (
                      <Form.Item required className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="tenant_deposit_charge_data"
                            rules={[
                              {
                                required: true,
                                message: t("systems.Error.cannotBeEmpty", {
                                  field: t(
                                    "domains.Tenant.tenantDepositCharge"
                                  ),
                                }),
                              },
                            ]}
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.tenantDepositCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 px:8 flex jc:center ai:center">
                            USDT +
                          </div>
                          <Form.Item
                            className="flex:1"
                            name="tenant_deposit_charge_rate"
                            rules={[
                              {
                                required: true,
                                message: t("systems.Error.cannotBeEmpty", {
                                  field: t(
                                    "domains.Tenant.tenantDepositCharge"
                                  ),
                                }),
                              },
                            ]}
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.tenantDepositCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            %
                          </div>
                        </div>
                      </Form.Item>
                    )}
                  </div>
                </Form.Item>

                <Form.Item
                  required
                  label={t("domains.Tenant.tenantDepositAmountRange")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="tenant_deposit_amount_min"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantDepositAmountMin"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.tenantDepositAmountMin"),
                        })}
                      />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center white-space:nowrap">
                      USDT ~
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="tenant_deposit_amount_max"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantDepositAmountMax"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.tenantDepositAmountMax"),
                        })}
                      />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center">
                      USDT
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  required
                  className="mb:0"
                  label={t("domains.Tenant.tenantDepositBlockThreshold")}
                >
                  <div className="flex">
                    <div className="h:30 pr:8 flex jc:center ai:center">
                      {t("systems.Extra.continuous")}
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="tenant_deposit_block_seconds"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t(
                              "domains.Tenant.tenantDepositBlockSeconds"
                            ),
                          }),
                        },
                      ]}
                    >
                      <Input type="number" />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center white-space:nowrap">
                      {t("systems.Extra.happenWithinSeconds")}
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="tenant_deposit_block_times"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantDepositBlockTimes"),
                          }),
                        },
                      ]}
                    >
                      <Input type="number" />
                    </Form.Item>

                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.times")}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  required
                  label={t("domains.Tenant.tenantDepositBlockSpan")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="tenant_deposit_block_span"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantDepositBlockSpan"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.tenantDepositBlockSpan"),
                        })}
                      />
                    </Form.Item>
                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.minute")}
                    </div>
                  </div>
                </Form.Item>
              </>
            ),
          },
          {
            label: t("domains.Tenant.tenantWithdraw"),
            key: "withdraw",
            forceRender: true,
            icon: <ToTopOutlined />,
            children: (
              <>
                <Form.Item
                  required
                  label={t("domains.Tenant.tenantWithdrawExpired")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="tenant_withdraw_expired"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantWithdrawExpired"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.tenantWithdrawExpired"),
                        })}
                      />
                    </Form.Item>
                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.minute")}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  required
                  label={t("domains.Tenant.tenantWithdrawRate")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="tenant_withdraw_rate_mode"
                    >
                      <Select onChange={handleWithdrawRateModeChange}>
                        <Select.Option value={0}>
                          {t("domains.Transfer.fixedExchangeRate")}
                        </Select.Option>
                        <Select.Option value={1}>
                          {t("domains.Transfer.followBito")}
                        </Select.Option>
                      </Select>
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center">
                      {withdrawRateModeText}
                    </div>

                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="tenant_withdraw_rate_data"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantWithdrawRate"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.tenantWithdrawRate"),
                        })}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>

                <Form.Item
                  required
                  label={t("domains.Tenant.tenantWithdrawCharge")}
                  className="mb:0"
                >
                  <div className="flex ai:center gap:16">
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="tenant_withdraw_charge_mode"
                    >
                      <Select onChange={handleWithdrawChargeModeChange}>
                        <Select.Option value={0}>
                          {t("domains.Transfer.unit")}
                        </Select.Option>
                        <Select.Option value={1}>
                          {t("domains.Transfer.ratio")}
                        </Select.Option>
                        <Select.Option value={2}>
                          {t("domains.Transfer.unitAndRatio")}
                        </Select.Option>
                      </Select>
                    </Form.Item>

                    {withdrawChargeMode === 0 && (
                      <Form.Item required className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="tenant_withdraw_charge_data"
                            rules={[
                              {
                                required: true,
                                message: t("systems.Error.cannotBeEmpty", {
                                  field: t(
                                    "domains.Tenant.tenantWithdrawCharge"
                                  ),
                                }),
                              },
                            ]}
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.tenantWithdrawCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            USDT
                          </div>
                        </div>
                      </Form.Item>
                    )}

                    {withdrawChargeMode === 1 && (
                      <Form.Item required className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="tenant_withdraw_charge_rate"
                            rules={[
                              {
                                required: true,
                                message: t("systems.Error.cannotBeEmpty", {
                                  field: t(
                                    "domains.Tenant.tenantWithdrawCharge"
                                  ),
                                }),
                              },
                            ]}
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.tenantWithdrawCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            %
                          </div>
                        </div>
                      </Form.Item>
                    )}

                    {withdrawChargeMode === 2 && (
                      <Form.Item required className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="tenant_withdraw_charge_data"
                            rules={[
                              {
                                required: true,
                                message: t("systems.Error.cannotBeEmpty", {
                                  field: t(
                                    "domains.Tenant.tenantWithdrawCharge"
                                  ),
                                }),
                              },
                            ]}
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.tenantWithdrawCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 px:8 flex jc:center ai:center">
                            USDT +
                          </div>
                          <Form.Item
                            className="flex:1"
                            name="tenant_withdraw_charge_rate"
                            rules={[
                              {
                                required: true,
                                message: t("systems.Error.cannotBeEmpty", {
                                  field: t(
                                    "domains.Tenant.tenantWithdrawCharge"
                                  ),
                                }),
                              },
                            ]}
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.tenantWithdrawCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            %
                          </div>
                        </div>
                      </Form.Item>
                    )}
                  </div>
                </Form.Item>

                <Form.Item
                  required
                  label={t("domains.Tenant.tenantWithdrawAmountRange")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="tenant_withdraw_amount_min"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantWithdrawAmountMin"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.tenantWithdrawAmountMin"),
                        })}
                      />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center white-space:nowrap">
                      USDT ~
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="tenant_withdraw_amount_max"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantWithdrawAmountMax"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.tenantWithdrawAmountMax"),
                        })}
                      />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center">
                      USDT
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  required
                  className="mb:0"
                  label={t("domains.Tenant.tenantWithdrawBlockThreshold")}
                >
                  <div className="flex">
                    <div className="h:30 pr:8 flex jc:center ai:center">
                      {t("systems.Extra.continuous")}
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="tenant_withdraw_block_seconds"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t(
                              "domains.Tenant.tenantWithdrawBlockSeconds"
                            ),
                          }),
                        },
                      ]}
                    >
                      <Input type="number" />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center white-space:nowrap">
                      {t("systems.Extra.happenWithinSeconds")}
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="tenant_withdraw_block_times"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantWithdrawBlockTimes"),
                          }),
                        },
                      ]}
                    >
                      <Input type="number" />
                    </Form.Item>

                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.times")}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  required
                  label={t("domains.Tenant.tenantWithdrawBlockSpan")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="tenant_withdraw_block_span"
                      rules={[
                        {
                          required: true,
                          message: t("systems.Error.cannotBeEmpty", {
                            field: t("domains.Tenant.tenantWithdrawBlockSpan"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.tenantWithdrawBlockSpan"),
                        })}
                      />
                    </Form.Item>
                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.minute")}
                    </div>
                  </div>
                </Form.Item>
              </>
            ),
          },
        ]}
        onChange={setActiveTabKey}
      ></Tabs>

      <Divider />

      <Form.Item>
        <div className="flex jc:end gap:8">
          <Button htmlType="button" onClick={onCancel}>
            {t("systems.Action.cancel")}
          </Button>
          <Button htmlType="button" onClick={onPrev}>
            {t("systems.Action.prev")}
          </Button>
          <Button type="primary" htmlType="submit">
            {t("systems.Action.next")}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};
